import { WallPost } from '@livekatsomo/models';
import { useDateFns } from '@livekatsomo/web/hooks';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'next-i18next';

/**
 * Props for the moderator view component.
 */
export interface ModeratorViewProps {
  /**
   * An array of wall posts to be displayed.
   */
  posts: WallPost[] | null;
  /**
   * A boolean indicating whether the component is currently loading data.
   */
  loading?: boolean;
}

/**
 * Displays a list of wall posts with moderation actions for each post.
 *
 * @param posts - An array of `WallPost` objects to display.
 * @param loading - A boolean indicating whether the component is currently loading data.
 */
export function WallsModeratorView({ posts, loading }: ModeratorViewProps) {
  const { t } = useTranslation();
  const { formatDistance } = useDateFns();

  return (
    <Container sx={{ paddingTop: 8, paddingBottom: 8 }} maxWidth="md">
      {loading ? (
        <CircularProgress
          aria-label={t('loading') || 'loading'}
        ></CircularProgress>
      ) : null}
      <Grid container spacing={4}>
        {posts &&
          posts.map((post) => (
            <Grid item key={post.id} xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardHeader
                  avatar={
                    <Avatar
                      aria-label={t('avatar')}
                      alt={post.external_fullname}
                      src={post.external_image_cdn ?? post.external_image}
                    ></Avatar>
                  }
                  title={post.external_fullname}
                  data-chromatic="ignore"
                  subheader={formatDistance(
                    new Date(post.created),
                    new Date(),
                    { addSuffix: true, includeSeconds: true },
                  )}
                />
                {post.post_image ? (
                  <CardMedia
                    sx={{ paddingTop: '56.25%' }}
                    image={post.post_image_cdn ?? post.post_image}
                    title={post.external_fullname}
                  />
                ) : null}
                <CardContent sx={{ flex: 1 }}>
                  <Typography
                    sx={[
                      !post.post_image && {
                        textAlign: 'center',
                        fontSize: '22px',
                        fontWeight: 400,
                        '&::before,&::after': {
                          display: 'block',
                          fontFamily: 'Georgia,serif',
                          fontWeight: 700,
                          fontSize: '50px',
                          lineHeight: 1,
                        },
                        '&::before': {
                          content: "'„'",
                          marginBottom: '15px',
                          marginTop: '-10px',
                        },
                        '&::after': {
                          content: "'“'",
                          marginTop: '15px',
                          marginBottom: '-20px',
                        },
                      },
                    ]}
                  >
                    {post.comment}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" color="primary">
                    {t('Hide')}
                  </Button>
                  <Button size="small" color="primary">
                    {t('Ban')}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
      </Grid>
    </Container>
  );
}

export default WallsModeratorView;
